import { useEffect, useState } from 'react';
import { AlertTriangle, LogIn } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import {
  readHandoffEmail,
  handoffLoginUrl,
  isMismatchDismissed,
  dismissMismatch,
  stripHandoffParam,
} from '@/lib/account-handoff';

/** True when the app is running inside an iframe (CRM embed). */
const isInIframe = (() => { try { return window.self !== window.top; } catch { return true; } })();

interface AccountHandoffBannerProps {
  /** Where the visitor came from, e.g. "the AppCursor extension". */
  sourceLabel?: string;
  /** Fired when the user accepts the mismatch ("Continue anyway"). Lets a host
   *  page react — the marketplace re-stamps its outgoing links with the
   *  now-accepted account instead of the one that was handed off. */
  onDismiss?: () => void;
}

/**
 * Warns when the account that sent the visitor here (`?suggestLoginMail=`)
 * isn't the one signed in on this property.
 *
 * Sibling of AccountMismatchBanner, and the two are deliberately mutually
 * exclusive so only one ever renders:
 *   - in an iframe  → AccountMismatchBanner, comparing against the CRM's user
 *   - top level     → this one, comparing against the hand-off param, and
 *                     routing to the full login page instead of an inline form
 *
 * The top-level case is how a marketplace "Install" link lands
 * (/{appCode}?suggestLoginMail=…) — same-tab, no CRM SDK to ask.
 */
export function AccountHandoffBanner({ sourceLabel = 'where you came from', onDismiss }: AccountHandoffBannerProps) {
  const user = useAuthStore((s) => s.user);
  const signOut = useAuthStore((s) => s.signOut);
  const [dismissed, setDismissed] = useState(false);
  const [switching, setSwitching] = useState(false);

  const expectedEmail = readHandoffEmail();
  const currentEmail = user?.email?.toLowerCase() || null;

  // Clear the param once the question is settled — it holds an email address,
  // and there's no reason to leave that in history / Referer / analytics URLs.
  //
  // Deliberately NOT cleared while a mismatch is still open: the param is what
  // re-raises the warning on reload. Also not cleared while signed out, where
  // it still feeds the Sign in and Install links. Runs after mount so the
  // one-shot readers (AuthGuard's memo, SaaS module init) have already read it.
  const resolved = !!expectedEmail && !!currentEmail
    && (expectedEmail === currentEmail || dismissed || isMismatchDismissed(currentEmail, expectedEmail));
  useEffect(() => {
    if (resolved) stripHandoffParam();
  }, [resolved]);

  if (isInIframe) return null;
  if (dismissed) return null;
  if (!expectedEmail || !currentEmail) return null;
  if (expectedEmail === currentEmail) return null;
  if (isMismatchDismissed(currentEmail, expectedEmail)) return null;

  const handleContinue = () => {
    dismissMismatch(currentEmail, expectedEmail);
    setDismissed(true);
    onDismiss?.();
  };

  // Must sign out first — LoginPage sends an already-authenticated visitor
  // straight back to `nextpage`, so linking to /login while the wrong account
  // is still active just bounces to this same page unchanged.
  //
  // `redirectTo` lets signOut do its own teardown (module disposal, proxy/IDB
  // caches belonging to the old account) and then hard-navigate, instead of the
  // default reload that would land back here. The target round-trips to this
  // exact page with the hand-off param intact, so the banner re-verifies after
  // the switch rather than trusting that the right account was used.
  const handleSwitchAccount = async () => {
    if (switching) return;
    setSwitching(true);
    try {
      await signOut({ redirectTo: handoffLoginUrl(expectedEmail) });
    } catch {
      setSwitching(false);
    }
  };

  return (
    // relative + z-[70] keeps the warning above the full-screen auth overlays
    // (SaaSAuthPrompt is `fixed inset-0 z-[60]` with a backdrop blur) — the
    // authorize step is exactly when knowing the account matters most.
    <div className="relative z-[70] shrink-0 flex flex-col sm:flex-row sm:items-center justify-center gap-2 sm:gap-4 bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 text-xs font-medium py-3 px-4">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        <span>
          Account mismatch — {sourceLabel} is <strong>{expectedEmail}</strong>, but you're signed in here as <strong>{currentEmail}</strong>
        </span>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <button
          onClick={handleSwitchAccount}
          disabled={switching}
          className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 dark:bg-amber-500 text-white px-3 py-1 text-xs font-medium hover:bg-amber-700 dark:hover:bg-amber-600 disabled:opacity-70 transition-colors"
        >
          <LogIn className="h-3 w-3" />
          {switching ? 'Signing out…' : `Sign in as ${expectedEmail}`}
        </button>
        <button
          onClick={handleContinue}
          disabled={switching}
          className="text-xs text-amber-700/70 dark:text-amber-300/70 hover:text-amber-800 dark:hover:text-amber-200 underline underline-offset-2 transition-colors"
        >
          Continue anyway
        </button>
      </div>
    </div>
  );
}
